import { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { toast } from "react-toastify";
import { Checkbox, FormControlLabel, TextField, Tooltip } from "@mui/material";

import AedasLoading from "../../Components/Shared/AedasLoading/AedasLoading";
import CustomModal from "../../Components/Shared/ModalCustom/CustomModal";
import { addRole, getRole, updateRole } from "../../Service/RolService/RolService";
import { apiErrorStatus, handleApiError } from "../../Utils/apiErrors";
import { MANAGE_USERS, permissionLabel } from "../../Utils/permissionsUtils";

// Alta (POST /rol) y edición (PUT /rol/{id}) de un rol con sus permisos.
// permission_ids es la lista completa y reemplaza a la anterior.

type FormValues = { name: string; permission_ids: number[] };

const EMPTY: FormValues = { name: "", permission_ids: [] };

const sameIds = (a: number[], b: number[]) => a.length === b.length && a.every((id) => b.includes(id));

const validationSchema = yup.object({
  name: yup.string().trim().required("Este campo es requerido"),
});

type RoleFormModalProps = {
  roleId: number | null; // null = alta
  permissions: any[] | null; // todos los permisos (GET /permission)
  isSelfRole: boolean; // rol del usuario actual: no se puede quitar gestionar_usuarios
  onClose: () => void;
  onChanged: () => void;
};

export default function RoleFormModal(props: RoleFormModalProps) {
  const { roleId, permissions, isSelfRole, onClose, onChanged } = props;
  const isEdit = roleId !== null;

  const [initial, setInitial] = useState<FormValues>(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (roleId === null) return;
    getRole(roleId)
      .then((response) =>
        setInitial({
          name: response.data.name ?? "",
          permission_ids: (response.data.permissions ?? []).map((permission: any) => permission.id),
        })
      )
      .catch((error) => {
        handleApiError(error, { fallback: "No se ha podido cargar el rol" });
        if (apiErrorStatus(error) === 404) onChanged();
        onClose();
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleId]);

  const formik = useFormik<FormValues>({
    initialValues: initial,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values, { setErrors }) => {
      const name = values.name.trim();
      const request: { name?: string; permission_ids?: number[] } = {};
      if (!isEdit || name !== initial.name) request.name = name;
      if (!isEdit || !sameIds(values.permission_ids, initial.permission_ids)) request.permission_ids = values.permission_ids;
      if (isEdit && Object.keys(request).length === 0) {
        toast.info("No hay cambios que guardar");
        onClose();
        return;
      }
      setSaving(true);
      (isEdit ? updateRole(roleId!, request) : addRole({ name, permission_ids: values.permission_ids }))
        .then(() => {
          toast.success(isEdit ? "Rol actualizado" : "Rol creado");
          onChanged();
          onClose();
        })
        .catch((error) =>
          handleApiError(error, {
            fallback: "No se ha podido guardar el rol",
            setFieldErrors: (errors) => setErrors(errors as any),
            onNotFound: () => {
              onChanged();
              onClose();
            },
          })
        )
        .finally(() => setSaving(false));
    },
  });

  const nameError = formik.errors.name && (formik.touched.name || formik.submitCount > 0) ? formik.errors.name : null;

  const togglePermission = (id: number, checked: boolean) => {
    const current = formik.values.permission_ids;
    formik.setFieldValue("permission_ids", checked ? [...current, id] : current.filter((value) => value !== id));
  };

  // En el propio rol no se puede desmarcar gestionar_usuarios (el back lo rechaza con 409)
  const isLocked = (permission: any) =>
    isSelfRole && permission.name === MANAGE_USERS && initial.permission_ids.includes(permission.id);

  const sortedPermissions = [...(permissions ?? [])].sort((a, b) =>
    permissionLabel(a.name).localeCompare(permissionLabel(b.name), "es")
  );

  const body = loading ? (
    <AedasLoading />
  ) : (
    <form className="aedasForm" onSubmit={formik.handleSubmit} noValidate>
      <TextField
        className="aedasForm__full"
        id="role-name"
        name="name"
        label="Nombre"
        required
        value={formik.values.name}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={Boolean(nameError)}
        helperText={nameError}
      />

      <section className="detailSection aedasForm__full">
        <h3 className="detailSectionTitle">
          Permisos ({formik.values.permission_ids.length} de {sortedPermissions.length})
        </h3>
        {sortedPermissions.length === 0 ? (
          <span className="cellEmpty">No hay permisos</span>
        ) : (
          <div className="permissionList">
            {sortedPermissions.map((permission) => {
              const locked = isLocked(permission);
              const control = (
                <FormControlLabel
                  label={permissionLabel(permission.name)}
                  disabled={locked}
                  control={
                    <Checkbox
                      id={`role-permission-${permission.id}`}
                      checked={formik.values.permission_ids.includes(permission.id)}
                      onChange={(e) => togglePermission(permission.id, e.target.checked)}
                    />
                  }
                />
              );
              return locked ? (
                <Tooltip key={permission.id} title="No puedes quitar este permiso a tu propio rol" placement="top-start">
                  <span>{control}</span>
                </Tooltip>
              ) : (
                <span key={permission.id}>{control}</span>
              );
            })}
          </div>
        )}
      </section>
      <button type="submit" hidden />
    </form>
  );

  return (
    <CustomModal
      size="lg"
      show
      onChange={onClose}
      title={isEdit ? "Editar rol" : "Añadir rol"}
      body={body}
      buttons={[
        { content: "Cancelar", primary: false, action: onClose },
        { content: "Guardar", primary: true, action: formik.submitForm, disabled: loading || saving },
      ]}
    />
  );
}
